import { Star } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import { useStarClient, useStarProject } from '@/api/generated/clients.ts';
import type { Client, Project } from '@/api/generated/schemas.ts';
import IconButton from '@/core/components/IconButton.tsx';
import Tooltip from '@/core/components/Tooltip.tsx';
import { classnames } from '@/utils/classnames.ts';

interface StarButtonProps {
    isStarred: boolean;
    onChange: (isStarred: boolean) => void;
    // Unstarred, it shows only while the `group/star` around it is hovered or it has focus, e.g. on a card.
    isShownOnHover?: boolean;
    className?: string;
}

// The member's own star for a client or project, which puts it in their rail (plan/DECISIONS.md D127).
const StarButton = ({ isStarred, onChange, isShownOnHover = false, className }: StarButtonProps) => {
    const { t } = useTranslation();
    const label = isStarred ? t('clients.star.remove') : t('clients.star.add');

    return (
        <Tooltip label={label}>
            <IconButton
                asChild
                aria-label={label}
                className={classnames(
                    isShownOnHover &&
                        !isStarred &&
                        'opacity-0 group-hover/star:opacity-100 data-[focus-visible]:opacity-100',
                    className
                )}
            >
                <AriaButton onPress={() => onChange(!isStarred)}>
                    <Star className={classnames(isStarred && 'fill-current text-text')} />
                </AriaButton>
            </IconButton>
        </Tooltip>
    );
};

// While the change is on its way, the star shows it, so it doesn't flick back until the clients are fetched again.
export const ClientStarButton = ({
    client,
    ...props
}: { client: Client } & Omit<StarButtonProps, 'isStarred' | 'onChange'>) => {
    const starClient = useStarClient();
    return (
        <StarButton
            {...props}
            isStarred={starClient.isPending ? starClient.variables.data.is_starred : client.is_starred}
            onChange={(isStarred) => starClient.mutate({ clientId: client.id, data: { is_starred: isStarred } })}
        />
    );
};

export const ProjectStarButton = ({
    project,
    ...props
}: { project: Project } & Omit<StarButtonProps, 'isStarred' | 'onChange'>) => {
    const starProject = useStarProject();
    return (
        <StarButton
            {...props}
            isStarred={starProject.isPending ? starProject.variables.data.is_starred : project.is_starred}
            onChange={(isStarred) => starProject.mutate({ projectId: project.id, data: { is_starred: isStarred } })}
        />
    );
};

export default StarButton;
